import { format } from "date-fns";
import { LuDownload } from "react-icons/lu";
import Cookies from "js-cookie";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AxiosError } from "axios";

import { Badge } from "@/components/ui/badge/badge";
import { cn } from "@/lib/utils";
import { useViewModal } from "./use-view-modal";
import { Button } from "@/components/ui/button/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select/select";
import { takeActionOnLeaveApplication } from "@/config/mutation/admin";
import { ChevronRight } from "lucide-react";
import { Separator } from "@/components/ui/separator/separator";

const classes: {
  [key: string]: string;
} = {
  Approved: `bg-blue-600 text-white font-normal`,
  Pending: `bg-yellow-500 text-black font-normal`,
  Rejected: `bg-red-500 text-white font-normal`,
  Cancelled: `bg-indigo text-white font-normal`,
};

export const ViewLeaveApplicationModal = () => {
  const queryClient = useQueryClient();
  const key = Cookies.get("key") as string;
  const { open, onOpenChange, leaveApplication } = useViewModal();

  const [takeActionOpen, setTakeActionOpen] = useState(false);
  const [value, setValue] = useState("");

  const { mutate, isPending } = useMutation({
    mutationFn: takeActionOnLeaveApplication(key),
  });

  const submitAction = async () => {
    if (!value || value === undefined) {
      return toast.error("Please select an action first.");
    }

    mutate(
      {
        leaveId: leaveApplication?.id as string,
        status: value,
      },
      {
        onSuccess: async () => {
          // console.log("New Employee", data);
          await queryClient.invalidateQueries({
            queryKey: ["leave-applications"],
          });

          toast.success("Action Taken.");
          onOpenChange(false);
          setTakeActionOpen(false);
        },

        onError: (error) => {
          console.log("ERROR WHILE CREATING NEW EMPLOYEE", error);
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className={cn(
            "transition-all duration-300",
            takeActionOpen && "max-w-5xl"
          )}
        >
          <DialogHeader>
            <DialogTitle className="tracking-normal flex items-center gap-3">
              {leaveApplication?.leaveType}
              <div className="">
                <Button
                  size="sm"
                  onClick={() => setTakeActionOpen((prev) => !prev)}
                >
                  Take Action
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="flex">
            <div className="mt-5 shrink-0 pr-10">
              <div className="flex flex-row items-center gap-2">
                <Badge
                  className={cn(classes[leaveApplication?.status as string])}
                >
                  {leaveApplication?.status}
                </Badge>
                <span>•</span>

                <div className="flex flex-col gap-2">
                  {leaveApplication?.startDate && leaveApplication?.endDate && (
                    <div className="flex flex-row items-center text-sm">
                      {format(
                        new Date(leaveApplication?.startDate as string),
                        "PPP"
                      )}{" "}
                      -{" "}
                      {format(
                        new Date(leaveApplication?.endDate as string),
                        "PPP"
                      )}
                    </div>
                  )}
                  <div className="flex flex-row items-center text-sm">
                    <span>
                      {leaveApplication?.employeeName} (
                      {leaveApplication?.employeeEmail})
                    </span>
                  </div>
                </div>
              </div>

              <Separator className="my-3" />

              <div className="flex flex-col gap-1 mt-3">
                <div className="flex flex-row items-center gap-2">
                  <ChevronRight className="w-4 h-4" />
                  <p>
                    {leaveApplication?.isPaid
                      ? "This leave is chosen to be a paid leave."
                      : "This leave is chosen to be a unpaid leave."}
                  </p>
                </div>
                <div className="flex flex-row items-center gap-2">
                  <ChevronRight className="w-4 h-4" />
                  <p>
                    {leaveApplication?.isHalfDay
                      ? "This leave is chosen to be for a half day."
                      : "This leave is chosen to be for full day."}
                  </p>
                </div>
              </div>

              <p className="mt-3">{leaveApplication?.reason}</p>

              <div className="flex mt-3">
                <Button
                  size="sm"
                  variant="outline"
                  className="flex items-center gap-1 font-normal"
                  asChild
                >
                  <a
                    href={leaveApplication?.attachment}
                    download="Download Attachment"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <LuDownload /> Attachment
                  </a>
                </Button>
              </div>
            </div>

            {takeActionOpen && (
              <div className="w-full border-l px-5 py-2 flex flex-col gap-3">
                <h2 className="text-lg font-medium">
                  Take action on leave application
                </h2>

                <div className="mt-5">
                  <Select
                    value={value}
                    onValueChange={setValue}
                    disabled={isPending}
                  >
                    <span>Choose Action</span>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Action..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Approved">Approved</SelectItem>
                      <SelectItem value="Rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-end">
                  <Button onClick={submitAction} disabled={isPending}>
                    Submit
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
